import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaLink, FaGoogle, FaLock } from 'react-icons/fa';
import { MdOutlineSwitchAccount } from "react-icons/md";
import { useAuth } from '../contexts/AuthContext';
import './AccountLinking.css';

const AccountLinking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState('');
  const [loadingLink, setLoadingLink] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  const email = searchParams.get('email');
  const existingProvider = searchParams.get('provider');

  useEffect(() => {
    // Add a small delay to ensure URL parameters are properly loaded
    const timer = setTimeout(() => {
      if (!email || !existingProvider) {
        console.log(' Missing URL parameters - email:', email, 'provider:', existingProvider);
        navigate('/login');
      } else {
        console.log(' URL parameters loaded - email:', email, 'provider:', existingProvider);
        setIsLoading(false);
      }
    }, 200); // Small delay to ensure URL parameters are available

    return () => clearTimeout(timer);
  }, [email, existingProvider, navigate]);

  const handleLinkAccounts = async () => {
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setLoadingLink(true);
    setError('');

    try {
      const response = await fetch('/api/auth/link-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          action: 'link',
          password
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        if (data.user) {
          // Set user in auth context immediately
          setUser(data.user);
          console.log(' User authenticated after account linking:', data.user.email);
          // Navigate to app
          navigate('/app');
        } else {
          // Fallback to login page if no user data returned
          console.log('⚠️ No user data returned, redirecting to login');
          navigate('/login');
        }
      } else {
        setError(data.error || 'Failed to link accounts');
      }
    } catch (err) {
      console.error('Account linking error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoadingLink(false);
    }
  };

  const handleUseDifferentAccount = () => {
    navigate('/login');
  };

  const handleContinueWithCurrent = async () => {
    setLoadingCreate(true);
    setError('');

    try {
      const response = await fetch('/api/auth/link-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          action: 'create-new'
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        if (data.user) {
          // Set user in auth context immediately
          setUser(data.user);
          console.log(' New user created and authenticated:', data.user.email);
          // Navigate to app
          navigate('/app');
        } else {
          // Fallback to login page if no user data returned
          console.log('⚠️ No user data returned, redirecting to login');
          navigate('/login');
        }
      } else {
        setError(data.error || 'Failed to create new account');
      }
    } catch (err) {
      console.error('Create new account error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoadingCreate(false);
    }
  };

  // Show loading while checking URL parameters
  if (isLoading) {
    return (
      <div className="account-linking-container">
        <div className="account-linking-card">
          <h2>Loading...</h2>
          <p>Preparing account linking options...</p>
        </div>
      </div>
    );
  }

  // Show error if parameters are missing
  if (!email || !existingProvider) {
    return (
      <div className="account-linking-container">
        <div className="account-linking-card">
          <h2>Invalid Request</h2>
          <p>Invalid account linking request. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-linking-container">
      <div className="account-linking-card">
        <h2>Account Already Exists</h2>
        <p className="account-linking-description">
          An account with <strong>{email}</strong> already exists using {existingProvider} login.
        </p>
        
        <div className="account-linking-options">
          <div className="linking-option">
            <h3>
              <FaLink className="option-icon google" />
              Option 1: Link Your Accounts
            </h3>
            <p>Connect your existing account with Google login</p>
            <div className="password-input">
              <FaLock className="input-icon" />
              <input
                type="password"
                placeholder="Enter your current password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLinkAccounts()}
              />
            </div>
            <button 
              className="btn-account-linking"
              onClick={handleLinkAccounts}
              disabled={loadingLink}
            >
              {loadingLink ? 'Linking...' : 'Link Accounts'}
            </button>
          </div>
          
          <div className="linking-option">
            <h3>
              <FaGoogle className="option-icon google" />
              Option 2: Use Different Google Account
            </h3>
            <p>Sign in with a different Google account</p>
            <button 
              className="btn-account-linking"
              onClick={handleUseDifferentAccount}
              style={{ backgroundColor: '#4285f4', color: '#ffffff' }}
            >
              Try Different Account
            </button>
          </div>
          
          <div className="linking-option">
            <h3>
              <MdOutlineSwitchAccount className="option-icon email" />
              Option 3: Create Separate Account
            </h3>
            <p>Create a new Google account with separate data from your existing account</p>
            <button 
              className="btn-account-linking"
              style={{ backgroundColor: '#4285f4', color: '#ffffff' }}
              onClick={handleContinueWithCurrent}
              disabled={loadingCreate}
            >
              {loadingCreate ? 'Creating...' : 'Create Separate Account'}
            </button>
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountLinking; 